import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { IconFileText, IconPlus, IconDownload } from "@tabler/icons-react"
import { subWeeks, startOfDay, endOfDay, format } from "date-fns"
import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { Input as CustomInput } from "@/components/Input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"
import { Chip } from "@/components/Chip"
import { ListTooltip, type ListTooltipItem } from "@/components/ListTooltip"
import { LuUsers, LuChevronDown, LuSearch, LuMinus, LuCheck } from "react-icons/lu"
import { DateRangePicker } from "@/components/CustomDatePicker/DateRangePicker"
import type { DateRange } from "@/components/filters/date-range-picker"
import { ModelsSelector, ALL_MODELS_SELECTION } from "@/components/ModelsSelector"
import type { ModelSelection } from "@/data/types/common.types"
import { InlineMetadataFilters, type AppliedFilter } from "@/components/FiltersSelector"
import { Checkbox } from "@/components/ui/checkbox"
import { useCreateReport, useReportDownloadUrl, useModels } from "@/data/hooks/api"
import type { GeneratedReport, TeamMember, CreateReportRequest, ReportPayload, MetadataFilter } from "./types"

// Recipients Selector Component
interface RecipientsSelectorProps {
  value: string[]
  onChange: (userIds: string[]) => void
  members: TeamMember[]
  loading: boolean
}

function RecipientsSelector({ value, onChange, members, loading }: RecipientsSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const selectedMembers = members.filter((m) => value.includes(m.id))
  const selectedCount = selectedMembers.length

  const filteredMembers = searchQuery
    ? members.filter(
      (m) =>
        m.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : members

  const isAllSelected = members.length > 0 && selectedCount === members.length
  const isNoneSelected = selectedCount === 0

  const handleToggleMember = (userId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!userId) return // Prevent adding null/undefined IDs

    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId))
    } else {
      onChange([...value, userId])
    }
  }

  const handleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange(members.map((m) => m.id))
  }

  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
  }

  const getDisplayText = () => {
    if (loading) return "Loading..."
    if (isAllSelected) return "All Recipients"
    if (isNoneSelected) return "No Recipients"
    return `${selectedCount} Recipient${selectedCount === 1 ? "" : "s"}`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          disabled={loading}
          className={cn(
            "hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]",
            "focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2",
            open && "bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0"
          )}
        >
          <LuUsers className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              "size-3.5 ml-0.5 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        align="start"
        sideOffset={6}
      >
        {/* Header with Search and Clear */}
        <div className="p-3 border-b border-[#EDEEF3] shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8A8F98]" />
              <input
                type="text"
                placeholder="Search recipients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[30px] pl-9 pr-3 rounded-[6px] border border-[#EDEEF3] bg-white text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus:outline-none focus:border-[#654EDA] transition-colors"
              />
            </div>
            {!loading && selectedCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              Loading recipients...
            </div>
          ) : members.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No team members available
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No recipients found
            </div>
          ) : (
            <>
              {/* Select All Option */}
              {!searchQuery && (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
                  onClick={handleSelectAll}
                >
                  <div className="relative">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={() => handleSelectAll()}
                      className={cn(
                        "size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]",
                        !isAllSelected && !isNoneSelected && "data-[state=unchecked]:bg-[#654EDA]/20 data-[state=unchecked]:border-[#654EDA]"
                      )}
                    />
                    {!isAllSelected && !isNoneSelected && (
                      <LuMinus className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-3 text-[#654EDA]" />
                    )}
                  </div>
                  <LuUsers className="size-4 text-[#8A8F98]" />
                  <span className="text-[13px] font-medium text-[#08090A]">
                    Select All
                  </span>
                  <span className="ml-auto text-[12px] text-[#8A8F98]">
                    {selectedCount}/{members.length}
                  </span>
                </div>
              )}

              {/* Recipients List */}
              {filteredMembers.map((member) => {
                const isSelected = value.includes(member.id)
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3] last:border-b-0"
                    onClick={(e) => handleToggleMember(member.id, e)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleMember(member.id)}
                      className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]"
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[13px] text-[#08090A] truncate">
                        {member.first_name} {member.last_name}
                      </span>
                      <span className="text-[11px] text-[#8A8F98] truncate">
                        {member.email}
                      </span>
                    </div>
                    {isSelected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Status config helper
const getStatusConfig = (status: GeneratedReport["status"]) => {
  switch (status) {
    case "complete":
      return { label: "Complete", dotColor: "#2ECC71" }
    case "pending":
      return { label: "Pending", dotColor: "#F1C40F" }
    case "in_progress":
      return { label: "In Progress", dotColor: "#F1C40F" }
    case "failed":
      return { label: "Failed", dotColor: "#E74C3C" }
  }
}

const getSourceLabel = (source: GeneratedReport["source"]) => {
  return source === "on_demand" ? "On Demand" : "Scheduled"
}

interface GeneratedReportsProps {
  reports: GeneratedReport[]
  total: number
  pageIndex: number
  pageSize: number
  onPageChange: (pageIndex: number) => void
  onPageSizeChange: (pageSize: number) => void
  teamMembers: TeamMember[]
  loadingMembers: boolean
  loading: boolean
  onReportCreated?: () => void
}

export function GeneratedReports({
  reports,
  total,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  teamMembers,
  loadingMembers,
  loading,
  onReportCreated,
}: GeneratedReportsProps) {
  // Create dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // API mutations
  const createReportMutation = useCreateReport()
  const downloadUrlMutation = useReportDownloadUrl()

  // Fetch models for default selection
  const { data: modelGroups } = useModels()
  const allModels = modelGroups ? modelGroups.flatMap((g) => g.models) : []

  // Form state
  const [formName, setFormName] = useState("")
  const [formDateRange, setFormDateRange] = useState<DateRange>({
    from: startOfDay(subWeeks(new Date(), 2)),
    to: endOfDay(new Date()),
  })
  const [formModels, setFormModels] = useState<ModelSelection>(ALL_MODELS_SELECTION)
  const [formMetadataFilters, setFormMetadataFilters] = useState<AppliedFilter[]>([])
  const [formRecipients, setFormRecipients] = useState<string[]>([])

  // Auto-select all models when dialog opens (for new reports only)
  useEffect(() => {
    if (isCreateDialogOpen && allModels.length > 0 && !formModels.all && formModels.providers.length === 0 && formModels.models.length === 0) {
      setFormModels(ALL_MODELS_SELECTION)
    }
  }, [isCreateDialogOpen, allModels.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setFormName("")
    setFormDateRange({
      from: startOfDay(subWeeks(new Date(), 2)),
      to: endOfDay(new Date()),
    })
    setFormModels(ALL_MODELS_SELECTION) // Reset to all models selected
    setFormMetadataFilters([])
    setFormRecipients([])
  }

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false)
    resetForm()
  }

  const handleCreateReport = async () => {
    if (!formName.trim()) {
      toast.error("Report name is required")
      return
    }

    if (!formDateRange.from || !formDateRange.to) {
      toast.error("Date range is required")
      return
    }

    if (formRecipients.length === 0) {
      toast.error("Please select at least one recipient")
      return
    }

    // Build metadata filters for API
    const metadataFilters: MetadataFilter[] | null = formMetadataFilters.length > 0
      ? formMetadataFilters.map((f) => ({
        key: f.tagName,
        operator: f.operation === "is" ? "eq" as const : "neq" as const,
        value: f.value,
      }))
      : null

    // Build payload
    const payload: ReportPayload = {
      report_type: "transactions",
      model_selection: formModels,
      metadata_filters: metadataFilters,
    }

    const request: CreateReportRequest = {
      name: formName,
      start_date: formDateRange.from.toISOString(),
      end_date: formDateRange.to.toISOString(),
      payload,
      recipient_user_ids: formRecipients,
    }

    try {
      await createReportMutation.mutateAsync(request)
      toast.success("Report generation started")
      handleCloseCreateDialog()
      onReportCreated?.()
    } catch (error) {
      toast.error("Failed to create report")
      console.error("Failed to create report:", error)
    }
  }

  const handleDownload = async (report: GeneratedReport) => {
    try {
      const response = await downloadUrlMutation.mutateAsync(report.id)
      // Open the download URL in a new tab
      window.open(response.download_url, "_blank")
      toast.success(`Downloading ${report.name}...`)
    } catch (error) {
      toast.error("Failed to get download link")
      console.error("Failed to get download URL:", error)
    }
  }

  // Column definitions
  const columns: DataTableColumn<GeneratedReport>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      flex: 1.5,
      cell: ({ row }) => (
        <span className="font-normal text-[#08090A]">{row.original.name}</span>
      ),
    },
    {
      id: "date_range",
      header: "Date Range",
      flex: 1.2,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">
          {format(new Date(row.original.start_date), "MMM d, yyyy")} - {format(new Date(row.original.end_date), "MMM d, yyyy")}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Created",
      accessorKey: "created_at",
      flex: 0.7,
      cell: ({ row }) => <span className="text-[#8A8F98]">{formatDate(row.original.created_at)}</span>,
    },
    {
      id: "recipients",
      header: "Recipients",
      flex: 0.8,
      cell: ({ row }) => {
        const recipients = row.original.recipients
        const tooltipItems: ListTooltipItem[] = recipients.map((r) => ({
          id: r.user_id,
          label: `${r.first_name} ${r.last_name}`,
        }))

        return (
          <ListTooltip
            items={tooltipItems}
            emptyMessage="No recipients"
            side="bottom"
            align="start"
          >
            <div className="cursor-default">
              <Chip
                label={`Recipients (${recipients.length})`}
                icon={<LuUsers className="size-3 text-[#8A8F98]" />}
              />
            </div>
          </ListTooltip>
        )
      },
    },
    {
      id: "status",
      header: "Status",
      flex: 0.7,
      cell: ({ row }) => {
        const config = getStatusConfig(row.original.status)
        return <Chip label={config.label} showDot dotColor={config.dotColor} />
      },
    },
    {
      id: "source",
      header: "Source",
      flex: 0.8,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">{getSourceLabel(row.original.source)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      flex: 0.3,
      enableSorting: false,
      cell: ({ row }) => {
        const report = row.original
        const isDownloading = downloadUrlMutation.isPending
        return (
          <div className="flex items-center justify-end">
            <button
              onClick={() => handleDownload(report)}
              disabled={report.status !== "complete" || isDownloading}
              className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#8A8F98]"
            >
              <IconDownload className="size-4" />
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="space-y-[24px]">
        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">All Reports</h2>
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
            >
              <IconPlus className="size-4" />
              Generate Report
            </Button>
          </div>
          <DataTable
            data={reports}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            emptyMessage="No reports generated yet"
            emptyIcon={<IconFileText className="size-6 text-[#8A8F98]" />}
            pagination={{
              enabled: true,
              mode: "server",
              pageIndex,
              pageSize,
              totalItems: total,
              onPageChange,
              onPageSizeChange,
              pageSizeOptions: [5, 10, 20],
            }}
          />
        </div>
      </div>

      {/* Generate Report Modal */}
      <Modal open={isCreateDialogOpen} onOpenChange={handleCloseCreateDialog}>
        <ModalContent className="max-w-[520px] max-h-[90vh] overflow-y-auto p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Generate Report</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Configure filters and recipients for your report. The report will be generated in the background.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[20px]">
            {/* Name */}
            <div className="space-y-[8px]">
              <Label htmlFor="report-name" className="text-[13px] text-[#08090A]">Report Name *</Label>
              <CustomInput
                id="report-name"
                size="sm"
                placeholder="e.g., Monthly Cost Report"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                disabled={createReportMutation.isPending}
                className="w-full"
              />
            </div>

            {/* Date Range */}
            <div className="space-y-[8px]">
              <Label className="text-[13px] text-[#08090A]">Date Range *</Label>
              <DateRangePicker
                value={formDateRange}
                onChange={(range) => {
                  if (range) {
                    setFormDateRange(range)
                  }
                }}
              />
            </div>

            {/* Models Filter */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filter by Models</Label>
              <div>
                <ModelsSelector value={formModels} onChange={setFormModels} />
              </div>
            </div>

            {/* Metadata Filters */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filters</Label>
              <InlineMetadataFilters
                value={formMetadataFilters}
                onChange={setFormMetadataFilters}
                addButtonLabel="Add Filter"
              />
            </div>

            {/* Recipients */}
            <div className="space-y-[8px]">
              <Label className="text-[13px] text-[#08090A]">Recipients *</Label>
              <RecipientsSelector
                value={formRecipients}
                onChange={setFormRecipients}
                members={teamMembers}
                loading={loadingMembers}
              />
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              onClick={handleCloseCreateDialog}
              disabled={createReportMutation.isPending}
              size="sm"
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateReport}
              disabled={createReportMutation.isPending}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {createReportMutation.isPending ? "Generating..." : "Generate Report"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
